import React, {useEffect, useState} from 'react'
import {Controller, useForm} from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs, {Dayjs} from "dayjs";
import 'dayjs/locale/en-gb';
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import {firstLevelSupport, FormIds} from "../../../constants";
import InCareNetHFService from "../../../services/InCareNetHFService";
import {useTranslation} from "react-i18next";
import {getEnumKeys, System} from "../../../utils/System";
import {fetchFeatureSwitchStates, refreshFeatureSwitchStates} from "../../../api/software";
import {FeatureToggleStates} from "sharedlibs";
import {useUserRole} from "../../../utils/UserRoleContext";

export interface InCareNetHFFormValues {
  id: string,
  url: string
  token: string
  tmzActiveSince: Dayjs | null
  priceImplantsPerPatient: string
  priceSensorsPerPatient: string
  os: string
  getemedHotlineSupport: boolean
  system: string
  disabled: boolean
  contractTerminated: boolean
  contractTerminatedAt?: string | null
  plannedDeactivationDate: Dayjs | null
  pauseUntil?: Dayjs | null
  disabledAt: Dayjs | null
  nonGBASensorPrice: number
  nonGBAImplantPrice: number,
  implantCorrection: number,
  extSensorsCorrection: number,
  salesPersonId: string
  // Read-only: synced from InCareNet HF (ICNDataCollectorDetails), not editable here.
  medtronicUsername?: string
}

// Strips a fetched response down to the plain toggle fields. fetchFeatureSwitchStates/
// refreshFeatureSwitchStates return CachedFeatureToggleStates (adds a runtime `fetchedAt`);
// featureStates is typed as FeatureToggleStates and must actually only ever carry those four
// fields, or a stray fetchedAt rides along through every spread (handleFeatureSwitchChange,
// the parent's featureStates/initialFeatureStatesRef) and ends up leaking into e.g. the
// persisted business audit log.
function toToggleStates(states: FeatureToggleStates): FeatureToggleStates {
  return {
    inSuiteBilling: states.inSuiteBilling,
    bayerProject: states.bayerProject,
    questionnaires: states.questionnaires,
    coverLetter: states.coverLetter,
  }
}

export interface InCareNetHFProductFormProps {
  onSubmit: (data: InCareNetHFFormValues) => void
  /** This function updates the dirty state in the parents component */
  updateDirty: (dirty: boolean) => void,
  defaultValues: InCareNetHFFormValues
  // flag to determine whether this form is being used to create or edit a new entry
  // if true, featureSwitches will be disabled, for now only on editing
  isCreate: boolean,
  // Called on every change to the feature switch states (including a user toggling one) so the
  // parent can send them along when the inCareNetHF endpoint is triggered on submit.
  setFeatureSwitchStates?: (featureSwitchStates: FeatureToggleStates) => void,
  // Called only when the states were just loaded/synced from the server (initial mount fetch,
  // or a manual "Fetch now") — never on a user toggle. Lets the parent track a separate
  // "last confirmed on the ICN instance" baseline distinct from the current (possibly still
  // unsaved) value, so it can tell whether the admin actually changed anything before deciding
  // to push on save.
  onFeatureStatesLoaded?: (featureSwitchStates: FeatureToggleStates) => void,
  // Called once, synchronously, the instant this form mounts — before its own toggle states
  // are fetched. A customer can have several ICN instances (one tab each), but the parent
  // tracks only one shared featureStates/baseline; without this, switching to a new tab while
  // an old tab's baseline is still sitting there (or a new tab's own load then fails) leaves
  // the previous instance's toggle values in place, and a save on the new tab can silently
  // push them to the wrong instance's solId. The parent must clear its baseline to null here
  // so nothing is pushed until this tab's own load actually confirms one.
  onFeatureStatesReset?: () => void,
}

export default function InCareNetHFProductForm({
                                                 onSubmit,
                                                 defaultValues,
                                                 updateDirty,
                                                 isCreate,
                                                 setFeatureSwitchStates,
                                                 onFeatureStatesLoaded,
                                                 onFeatureStatesReset
                                               }: Readonly<InCareNetHFProductFormProps>) {
  const form = useForm<InCareNetHFFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    defaultValues
  })

  const {register, control, handleSubmit, formState, getValues, setValue, watch} = form

  // Triggers rerender on changed values in select
  const watchedOs = watch('os', getValues().os)
  const watchedSystem = watch('system', getValues().system)
  const watchedPlannedDeactivationDate = watch('plannedDeactivationDate')
  const watchedContractTerminated = watch('contractTerminated')

  const [featureStates, setFeatureStates] = React.useState<FeatureToggleStates>({
    inSuiteBilling: false,
    questionnaires: false,
    bayerProject: false,
    coverLetter: false
  })
  const [featureLoadError, setFeatureLoadError] = React.useState(false)
  const [featuresFetchedAt, setFeaturesFetchedAt] = React.useState<number | null>(null)
  const [isRefreshingFeatures, setIsRefreshingFeatures] = React.useState(false)
  const [featureRefreshError, setFeatureRefreshError] = React.useState(false)
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false)
  // True from the moment the admin flips a switch until the next successful load/refresh
  // confirms a new baseline. Gates "Fetch now": without this, a fetch overwrites featureStates
  // (and the parent's copy, via setFeatureSwitchStates) with the instance's current state,
  // silently discarding whatever the admin just toggled but hasn't saved yet.
  const [hasPendingToggleChange, setHasPendingToggleChange] = useState(false)
  const [discardToggleChangeDialogOpen, setDiscardToggleChangeDialogOpen] = useState(false)

  const {errors} = formState

  const {isAdmin} = useUserRole();

  const {t} = useTranslation()

  useEffect(() => {
    updateDirty(form.formState.isDirty)
  }, [form.formState.isDirty])

  // Only a persisted instance with a real url/token can be fetched from — matches what an
  // existing (non-create) record's required-field validation already guarantees, but a fresh
  // "Add product" form (isCreate) has neither yet. Shared by both the automatic load and the
  // manual "Fetch now" button so they can't drift out of sync with each other.
  const hasUrlAndToken = defaultValues.url.trim() !== '' && defaultValues.token.trim() !== ''

  // Guards every state/callback update that follows an await against firing after this specific
  // form instance is gone (tab switched away mid-fetch) — otherwise a slow response can still
  // land on the parent's shared featureStates/baseline after a different tab has taken over,
  // silently reattaching this instance's data to whichever tab is now active.
  const mountedRef = React.useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // Clear the parent's shared baseline immediately on mount, before this tab's own fetch has
    // a chance to resolve — so a stale baseline left over from a previous tab (or this tab's own
    // load failing) is never mistaken for this instance's confirmed state and pushed to it.
    onFeatureStatesReset?.()
    ;(async () => {
      await fetchFeatureToggles()
    })()
  }, []);

  async function fetchFeatureToggles(): Promise<void> {
    if (hasUrlAndToken) {
      try {
        const res = await fetchFeatureSwitchStates(defaultValues.id)
        if (!mountedRef.current) return
        const states = toToggleStates(res)
        setFeatureStates(states)
        setFeaturesFetchedAt(res.fetchedAt)
        setFeatureSwitchStates?.(states)
        onFeatureStatesLoaded?.(states)
        setHasPendingToggleChange(false)
      } catch {
        if (!mountedRef.current) return
        setFeatureLoadError(true)
      }
    }
  }

  async function handleRefreshFeatureToggles(): Promise<void> {
    if (!hasUrlAndToken) return
    if (hasPendingToggleChange) {
      setDiscardToggleChangeDialogOpen(true)
      return
    }
    await doRefreshFeatureToggles()
  }

  async function doRefreshFeatureToggles(): Promise<void> {
    setFeatureRefreshError(false)
    setIsRefreshingFeatures(true)
    try {
      const res = await refreshFeatureSwitchStates(defaultValues.id)
      if (!mountedRef.current) return
      const states = toToggleStates(res)
      setFeatureStates(states)
      setFeaturesFetchedAt(res.fetchedAt)
      setFeatureLoadError(false)
      setFeatureSwitchStates?.(states)
      onFeatureStatesLoaded?.(states)
      setHasPendingToggleChange(false)
    } catch {
      if (!mountedRef.current) return
      setFeatureRefreshError(true)
    } finally {
      if (mountedRef.current) setIsRefreshingFeatures(false)
    }
  }

  function handleFeatureSwitchChange(e: React.ChangeEvent<HTMLInputElement>) {
    updateDirty(true)
    setHasPendingToggleChange(true)
    let newState: FeatureToggleStates = featureStates
    switch (e.currentTarget.id) {
      case "inSuiteBillingFeatureSwitch":
        newState = { ...featureStates, inSuiteBilling: !featureStates.inSuiteBilling }
        break;
      case "questionnairesFeatureSwitch":
        newState = { ...featureStates, questionnaires: !featureStates.questionnaires }
        break;
      case "bayerProjectFeatureSwitch":
        newState = { ...featureStates, bayerProject: !featureStates.bayerProject }
        break;
      case "coverLetterFeatureSwitch":
        newState = { ...featureStates, coverLetter: !featureStates.coverLetter }
        break;
    }

    setFeatureStates(newState)

    if (newState) {
      if (setFeatureSwitchStates) {
        setFeatureSwitchStates(newState)
      }
    }
  }

  return (
    <form id={FormIds.updateInCareNetHF} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
            marginTop: 2,
            paddingTop: 2,
            paddingLeft: 2,
            paddingRight: 2,
            flexFlow: 'row wrap'
          }}>
        {/* Left form element*/}
        <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: '45%'}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
            <Stack spacing={3}>
              <TextField
                id={"url"}
                variant="standard"
                label={t('objects.products.sol.incarenethf.url')}
                required
                {...register("url", {
                  required: t('objects.products.sol.incarenethf.requiredMessage', {obj: t('objects.products.sol.incarenethf.url')}),
                  validate: InCareNetHFService.validateICNUrl,
                })}
                error={!!errors.url}
                helperText={errors.url?.message}
                disabled={!isAdmin}
              />
              <TextField
                id={"token"}
                variant="standard"
                label={t('objects.products.sol.incarenethf.token')}
                required
                {...register("token", {
                  required: t('objects.products.sol.incarenethf.requiredMessage', {obj: t('objects.products.sol.incarenethf.token')}),
                })}
                error={!!errors.token}
                helperText={errors.token?.message}
                disabled={!isAdmin}
              />
              {!isCreate && (
                <TextField
                  id={"medtronicUsername"}
                  variant="standard"
                  label={t('objects.products.sol.incarenethf.medtronicUsername')}
                  {...register("medtronicUsername")}
                  slotProps={{input: {readOnly: true}}}
                  helperText={t('objects.products.sol.incarenethf.medtronicUsernameHint')}
                />
              )}
              <Stack direction={"row"} spacing={1}>
                {/* Controller, not register + getValues(): getValues() does not re-render, so the
                    picker kept a stale `value` and showed a different date than the one saved. */}
                <Controller
                  name="tmzActiveSince"
                  control={control}
                  rules={{
                    required: t('objects.products.sol.incarenethf.requiredMessage', {obj: t('objects.products.sol.incarenethf.tmzActiveSince')}),
                  }}
                  render={({field}) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                      <DatePicker
                        label={t('objects.products.sol.incarenethf.tmzActiveSince')}
                        value={field.value}
                        inputRef={field.ref}
                        onChange={(value) => field.onChange(value)}
                        slotProps={{
                          textField: {
                            required: true,
                            onBlur: field.onBlur,
                            error: !!errors.tmzActiveSince,
                            helperText: errors.tmzActiveSince?.message,
                          },
                        }}
                        disabled={!isAdmin}
                      />
                    </LocalizationProvider>
                  )}
                />
                <TextField
                  id={"priceImplantsPerPatient"}
                  variant="outlined"
                  label={t('objects.products.sol.incarenethf.priceImplantsPerPatient')}
                  type="number"
                  required
                  slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
                  {...register("priceImplantsPerPatient", {
                    required: t('objects.products.sol.incarenethf.requiredMessage', {obj: t('objects.products.sol.incarenethf.priceImplantsPerPatient')}),
                  })}
                  error={!!errors.priceImplantsPerPatient}
                  helperText={errors.priceImplantsPerPatient?.message}
                  disabled={!isAdmin}
                />
                <TextField
                  id={"priceSensorsPerPatient"}
                  variant="outlined"
                  label={t('objects.products.sol.incarenethf.priceSensorsPerPatient')}
                  required
                  type="number"
                  slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
                  {...register("priceSensorsPerPatient", {
                    required: t('objects.products.sol.incarenethf.requiredMessage', {obj: t('objects.products.sol.incarenethf.priceSensorsPerPatient')}),
                  })}
                  error={!!errors.priceSensorsPerPatient}
                  helperText={errors.priceSensorsPerPatient?.message}
                  disabled={!isAdmin}
                />
              </Stack>
              <FormControl id={'form_control_os'} variant={'standard'} disabled={!isAdmin}>
                <InputLabel id={'form_os_label'}>{t('objects.products.sol.incarenethf.os')}</InputLabel>
                <Select
                  id={"form_os_selector"}
                  sx={{width: '300px'}}
                  labelId={'form_os_label'}
                  value={watchedOs}
                  onChange={event => setValue('os', event.target.value as string, {shouldDirty: true})}
                >
                  <MenuItem value={"el7"}>EL7</MenuItem>
                  <MenuItem value={"el8"}>EL8</MenuItem>
                  <MenuItem value={"el9"}>EL9</MenuItem>
                </Select>
              </FormControl>
              <FormControl disabled={!isAdmin}>
                <InputLabel id={'system_input_label'}>{t('objects.customer.system')}</InputLabel>
                <Select
                  sx={{width: "31.85rem"}}
                  value={watchedSystem}
                  onChange={event => setValue('system', event.target.value as string, {shouldDirty: true})}
                  label={'System'} //replace w translation
                  labelId={'system_input_label'}>
                  {
                    getEnumKeys(System).map((value) => {
                      return <MenuItem key={value} value={System[value]}>{System[value]}</MenuItem>
                    })
                  }
                </Select>
              </FormControl>
              <TextField
                id={"implantCorrection"}
                variant="outlined"
                label={t('objects.products.sol.incarenethf.implantCorrection')}
                required
                type="number"
                {...register("implantCorrection", {
                  required: t('objects.products.sol.incarenethf.warnings.implantCorrectionMissing'),
                  valueAsNumber: true
                })}
                error={!!errors.implantCorrection}
                helperText={errors.implantCorrection?.message}
                disabled={!isAdmin}
              />

              <TextField
                id={"extSensorsCorrection"}
                variant="outlined"
                label={t('objects.products.sol.incarenethf.extSensorsCorrection')}
                required
                type="number"
                {...register("extSensorsCorrection", {
                  required: t('objects.products.sol.incarenethf.warnings.extSensorsCorrectionMissing'),
                  valueAsNumber: true
                })}
                error={!!errors.extSensorsCorrection}
                helperText={errors.extSensorsCorrection?.message}
                disabled={!isAdmin}
              />
            </Stack>
          </Box>
        </Box>
        {/*//TODO add form changing*/}
        {/* Right form element*/}
        <Box sx={{width: '45%', display: 'flex', flexDirection: 'column'}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Typography variant={'h6'}>{t('objects.products.sol.incarenethf.featureToggles')}</Typography>
            <Button
              size={'small'}
              startIcon={isRefreshingFeatures ? <CircularProgress size={16}/> : <RefreshIcon/>}
              disabled={!isAdmin || isCreate || isRefreshingFeatures || !hasUrlAndToken}
              onClick={handleRefreshFeatureToggles}
            >
              {t('objects.products.sol.incarenethf.fetchFeatureToggles')}
            </Button>
          </Box>

          <Typography variant={'caption'} color={'text.secondary'}>
            {featuresFetchedAt
              ? t('objects.products.sol.incarenethf.featureTogglesLastFetched', {
                date: dayjs(featuresFetchedAt).format('DD.MM.YYYY HH:mm')
              })
              : t('objects.products.sol.incarenethf.featureTogglesNeverFetched')}
          </Typography>

          {featureLoadError && (
            <Alert severity="warning" sx={{mb: 1, mt: 1}}>
              {t('objects.products.sol.incarenethf.featureToggleLoadError')}
            </Alert>
          )}

          {featureRefreshError && (
            <Alert severity="warning" sx={{mb: 1, mt: 1}}>
              {t('objects.products.sol.incarenethf.featureToggleRefreshError')}
            </Alert>
          )}

          {!isCreate && !featureLoadError && featuresFetchedAt === null && (
            <Alert severity="info" sx={{mb: 1, mt: 1}}>
              {t('objects.products.sol.incarenethf.featureTogglesNoDataYet')}
            </Alert>
          )}

          <FormGroup sx={{display: 'flex', flexDirection: 'row'}}>
            <FormControlLabel
              control={<Switch disabled={!isAdmin || isCreate || featureLoadError || featuresFetchedAt === null} id={'inSuiteBillingFeatureSwitch'}
                               checked={featureStates.inSuiteBilling}
                               onChange={handleFeatureSwitchChange}/>}
              label={t('objects.products.sol.incarenethf.inSuiteBilling')}/>
            <FormControlLabel
              control={<Switch disabled={!isAdmin || isCreate || featureLoadError || featuresFetchedAt === null} id={'bayerProjectFeatureSwitch'} checked={featureStates.bayerProject}
                               onChange={handleFeatureSwitchChange}/>}
              label={t('objects.products.sol.incarenethf.bayerProject')}/>
            <FormControlLabel
              control={<Switch disabled={!isAdmin || isCreate || featureLoadError || featuresFetchedAt === null} id={'coverLetterFeatureSwitch'} checked={featureStates.coverLetter}
                               onChange={handleFeatureSwitchChange}/>}
              label={t('objects.products.sol.incarenethf.coverLetter')}/>
            <FormControlLabel
              control={<Switch disabled={!isAdmin || isCreate || featureLoadError || featuresFetchedAt === null} id={'questionnairesFeatureSwitch'}
                               checked={featureStates.questionnaires}
                               onChange={handleFeatureSwitchChange}/>}
              label={t('objects.products.sol.incarenethf.questionnaires')}/>
          </FormGroup>

          <Typography variant={'h6'}>Support</Typography>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <FormControl id={'form_active_support'} variant={'standard'} sx={{mr: 5}} disabled={!isAdmin}>
              <InputLabel
                id={'form_active_support_label'}>{t('objects.products.sol.incarenethf.firstLevelSupport')}</InputLabel>
              <Select
                sx={{width: 300}}
                labelId={'form_active_support_label'}
                id={'form_active_support_selector'}
              >
                {firstLevelSupport.map((support) => {
                  return <MenuItem value={support}>{support}</MenuItem>
                })}
              </Select>
            </FormControl>

            <Controller
              name={'getemedHotlineSupport'}
              control={control}
              render={({field}) => <FormControlLabel control={<Checkbox checked={Boolean(field.value)} {...field} disabled={!isAdmin} />}
                                                     label={t('objects.products.sol.incarenethf.getemedHotlineSupport')}/>}
            />
          </div>


          <Alert severity="info" sx={{mt: 2, mb: 1}}>
            {t('form.fields.salesperson.managedOnCustomerHint')}
          </Alert>

          <Typography sx={{mt: '5px'}} variant={'h6'}>Non-GBA Prices</Typography>
          <div style={{display: 'flex', marginTop: '5px', flexDirection: 'row'}}>
            <Stack spacing={3} sx={{width: '100%'}}>
              <TextField
                id={"nonGBAImplantPrice"}
                variant="outlined"
                label="Non-GBA Implant Preis"
                type="number"
                slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
                {...register("nonGBAImplantPrice", {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || isNaN(v) ? 0 : v)
                })}
                error={!!errors.nonGBAImplantPrice}
                helperText={errors.nonGBAImplantPrice?.message}
                disabled={!isAdmin}
              />

              <TextField
                id={"nonGBASensorPrice"}
                variant="outlined"
                label="Non-GBA Sensor Preis"
                type="number"
                slotProps={{input: {endAdornment: <InputAdornment position={'end'}>€</InputAdornment>}}}
                {...register("nonGBASensorPrice", {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || isNaN(v) ? 0 : v)
                })}
                error={!!errors.nonGBASensorPrice}
                helperText={errors.nonGBASensorPrice?.message}
                disabled={!isAdmin}
              />

            </Stack>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center'}}>
            <Controller
              name="disabled"
              control={control}
              disabled={!isAdmin}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      disabled={!!watchedPlannedDeactivationDate}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t('common.deactivated')}
                />
              )}
            />
            <Controller
              name="contractTerminated"
              control={control}
              disabled={!isAdmin}
              render={({field}) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTerminateDialogOpen(true)
                        } else {
                          field.onChange(false)
                        }
                      }}
                    />
                  }
                  label={t('objects.products.sol.incarenethf.contractTerminated')}
                />
              )}
            />
            {watchedContractTerminated && defaultValues.contractTerminatedAt && (
              <Typography variant="body2" sx={{color: 'text.secondary'}}>
                {t('objects.products.sol.incarenethf.contractTerminatedAt')}: {dayjs(defaultValues.contractTerminatedAt).format('DD/MM/YYYY')}
              </Typography>
            )}
          </div>

          <Dialog open={discardToggleChangeDialogOpen} onClose={() => setDiscardToggleChangeDialogOpen(false)}>
            <DialogTitle>{t('objects.products.sol.incarenethf.fetchFeatureTogglesDiscardTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('objects.products.sol.incarenethf.fetchFeatureTogglesDiscardBody')}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDiscardToggleChangeDialogOpen(false)}>{t('activities.cancel')}</Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDiscardToggleChangeDialogOpen(false)
                  void doRefreshFeatureToggles()
                }}
              >
                {t('objects.products.sol.incarenethf.fetchFeatureTogglesDiscardConfirm')}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={terminateDialogOpen} onClose={() => setTerminateDialogOpen(false)}>
            <DialogTitle>{t('objects.products.sol.incarenethf.terminateConfirmTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('objects.products.sol.incarenethf.terminateConfirmBody')}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTerminateDialogOpen(false)}>{t('activities.cancel')}</Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setValue('contractTerminated', true, { shouldDirty: true })
                  // With a planned deactivation date the contract terminates
                  // effective that date — the nightly job disables the
                  // instance then. Only force-disable when no date is set.
                  if (!getValues().plannedDeactivationDate) {
                    setValue('disabled', true, { shouldDirty: true })
                  }
                  setTerminateDialogOpen(false)
                }}
              >
                {t('activities.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
          <Controller
            name="plannedDeactivationDate"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true
                return value.isBefore(dayjs(), 'day')
                  ? t('objects.products.sol.incarenethf.plannedDeactivationDatePastError')
                  : true
              }
            }}
            render={({field}) => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label={t('objects.products.sol.incarenethf.plannedDeactivationDate')}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    if (value) {
                      setValue('disabled', false, { shouldDirty: true })
                    }
                  }}
                  slotProps={{
                    field: {clearable: true},
                    textField: {
                      error: !!errors.plannedDeactivationDate,
                      helperText: errors.plannedDeactivationDate?.message,
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
          <Controller
            name="pauseUntil"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true
                if (watchedPlannedDeactivationDate && value.isBefore(watchedPlannedDeactivationDate, 'day')) {
                  return t('objects.products.sol.incarenethf.reactivationDateAfterDeactivation')
                }
                if (value.isBefore(dayjs(), 'day')) {
                  return t('objects.products.sol.incarenethf.reactivationDateInFuture')
                }
                return true
              }
            }}
            render={({field}) => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label="Reactivation Date"
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  disabled={!isAdmin || !watchedPlannedDeactivationDate}
                  slotProps={{
                    field: {clearable: true},
                    textField: {
                      error: !!errors.pauseUntil,
                      helperText: errors.pauseUntil?.message,
                      sx: { mt: 2 },
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Box>
      </Box>
    </form>
  )
}
